// 면적 단위 변환(#173) — 입력 원본은 m², 평은 m²/3.305785 를 둘째 자리로 끊어 같이 쓴다.

import { AreaRequiredError } from "./errors";

/** 1평 = 400/121 m² ≈ 3.305785. */
export const SQM_PER_PYEONG = 3.305785;

const round2 = (v: number): number => Math.round(v * 100) / 100;

// 입력 상한 — 평 100,000 과 그 m²(330,578.5) 를 한 쌍으로 둔다(스키마 두 칸이 같이 쓴다).
export const AREA_PYEONG_MAX = 100_000;
export const AREA_M2_MAX = round2(AREA_PYEONG_MAX * SQM_PER_PYEONG);

const positive = (v: number | null | undefined): v is number =>
  typeof v === "number" && Number.isFinite(v) && v > 0;

/** m² → 평(numeric(10,2) 자리). 0 이하·없음은 null. */
export const m2ToPyeong = (m2: number | null | undefined): number | null => {
  if (!positive(m2)) return null;
  const p = round2(m2 / SQM_PER_PYEONG);
  return p > 0 ? p : null;
};

/** 평 → m²(numeric(10,2) 자리). 옛 견적(m² 없이 평만)을 m² 로 보일 때 쓴다. */
export const pyeongToM2 = (pyeong: number | null | undefined): number | null => {
  if (!positive(pyeong)) return null;
  const m = round2(pyeong * SQM_PER_PYEONG);
  return m > 0 ? m : null;
};

/** 견적의 m² — 원본이 있으면 그것, 없으면 평×3.305785. */
export const quoteAreaM2 = (q: { area_m2?: number | null; rough_area_pyeong: number | null }): number | null =>
  positive(q.area_m2) ? round2(q.area_m2) : pyeongToM2(q.rough_area_pyeong);

export interface AreaPair {
  area_m2: number | null;
  area_pyeong: number | null;
}

/** 입력 짝 맞추기 — m² 가 오면 평을 m² 에서 다시 계산하고, 평만 오면 m² 를 평에서 채운다 · undefined 는 「안 줌」 · 둘 다 있거나 둘 다 null 이다(0·평이 0 으로 끊기는 m² 는 null 쌍). */
export const areaPairOf = (
  area_m2: number | null | undefined,
  area_pyeong: number | null | undefined
): { area_m2?: number | null; area_pyeong?: number | null } => {
  const none = { area_m2: null, area_pyeong: null };
  if (area_m2 !== undefined) {
    const pyeong = m2ToPyeong(area_m2);
    return pyeong === null ? none : { area_m2: round2(area_m2 as number), area_pyeong: pyeong };
  }
  if (area_pyeong === undefined) return {};
  const m2 = pyeongToM2(area_pyeong);
  return m2 === null ? none : { area_m2: m2, area_pyeong: round2(area_pyeong as number) };
};

/** 가견적 저장 면적 — 둘 중 하나는 있어야 한다 · 인메모리·Supabase 가 같이 쓴다. */
export const roughAreaPairOf = (
  area_m2: number | null | undefined,
  area_pyeong: number | null | undefined
): { area_m2: number; area_pyeong: number } => {
  const pair = areaPairOf(area_m2, area_pyeong);
  if (!pair.area_pyeong || !pair.area_m2) throw new AreaRequiredError();
  return { area_m2: pair.area_m2, area_pyeong: pair.area_pyeong };
};
